/**
 * The Javascript Date constructor never throws an error, even when provided weird parameters.
 * However, they can be internally invalid.
 */
export function isValidDate(date: Date) {
  return !isNaN(date.getTime())
}

/**
 * Create a valid Date from an ISO 8601 string or returns undefined.
 */
export function dateFromISOString(str: string): Date | undefined {
  const date = new Date(str)
  return isValidDate(date) ? date : undefined
}

/**
 * Returns a human readable string of how long ago the specified date is from now.
 * @param date
 */
export function getDateDiffString(date: Date) {
  const now = new Date()
  const diff = now.getTime() - date.getTime()

  const minutes = Math.floor(diff / (60 * 1000))
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)

  if (days > 0)
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric"
    })
  if (hours > 0) return timeAgo({ count: hours, text: "hour" })
  if (minutes > 0) return timeAgo({ count: minutes, text: "minute" })
  return "Just now"
}

function timeAgo(params: { count: number; text: Intl.RelativeTimeFormatUnit }) {
  const { count, text } = params
  const relativeTime = new Intl.RelativeTimeFormat("en-US", { style: "short" })

  return relativeTime.format(-Math.abs(count), text)
}

/**
 * Should mainly be used for debugging or very rare formatting use cases.
 * Example output:  12 Sep, 2030 01:00 am
 */
export function prettyPrintDate(date: Date, locale = "en-US") {
  const dateFormatter = new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "short",
    day: "numeric"
  })

  const timeFormatter = new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit"
  })

  const datePart = dateFormatter.format(date)
  const timePart = timeFormatter.format(date).toLocaleLowerCase()

  return `${datePart} ${timePart}`
}
